import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router';

import {
  IonToolbar,
  IonContent,
  IonPage,
  IonTitle,
  IonMenuButton,
  IonButton,
  IonButtons,
  IonHeader,
  IonList,
  IonListHeader,
  IonItem,
  IonInput,
  IonLoading,
  IonSelect,
  IonSelectOption,
  getConfig,
  useIonToast,
  IonGrid,
  IonRow,
  IonCol,  
} from '@ionic/react';

import './InspectionPage.scss';
import { connect } from '../data/connect';
import { Inspection, RackNumInfo } from '../models/Inspection';
import InspectionList from '../components/InspectionList';
import InspectionCard from '../components/InspectionCard';
import axiosInstance from '../data/axios/axiosInstance';

interface OwnProps {
  onTabChange: (event: CustomEvent) => void;
}

interface StateProps {
  mode: 'ios' | 'md';
  isAuthenticated: boolean;
}

interface DispatchProps {
}

type InspectionPageProps = OwnProps & StateProps & DispatchProps;

const InspectionPage: React.FC<InspectionPageProps> = ({
  isAuthenticated,
  onTabChange,
}) => {
  const [barcode, setBarcode] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [inspection, setInspection] = useState<Inspection>();
  const [count, setCount] = useState(0);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [segment, setSegment] = useState("inspection");
  const [loading, setLoading] = useState(false);
  const pageRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const quantityRef = useRef<HTMLIonSelectElement>(null);
  const [presentToast] = useIonToast();
  const location = useLocation();
  
  const [workStarted, setWorkStarted] = useState(false);
  const [memoryData, setMemoryData] = useState<Inspection[]>([]);
  const [koStoreData, setKoStoreData] = useState<RackNumInfo[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [handledCount, setHandledCount] = useState(0);
  const [workMessage, setWorkMessage] = useState('');

  const showToast = (message: string, duration = 3000) => {
    presentToast({
      message,
      duration,
      position: 'middle',
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
            // Close 押下後にフォーカスを戻す
            setTimeout(() => {
              inputRef.current?.setFocus();
            }, 100);
          },          
        },
      ],
    });
  };

  useEffect(() => {
    inputRef.current?.setFocus();
  }, [count]);

  const playBuzzer = () => {
    const buzzer = document.getElementById('buzzer') as HTMLAudioElement;
    if (buzzer) {
      buzzer.play();
    }
  };

  const playRing = () => {
    const ring = document.getElementById('ring') as HTMLAudioElement;
    if (ring) {
      ring.play();
    }
  };

  const playDeliverOk = () => {
    const deliver_ok = document.getElementById('deliver_ok') as HTMLAudioElement;
    if (deliver_ok) {
      deliver_ok.play();
    }
  };

  const handleBarcodeChange = (event: CustomEvent) => {
    setBarcode((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleQuantityChange = (event: CustomEvent) => {
    setQuantity(Number((event.target as HTMLInputElement).value));
  };

  const searchBarCode = () => {
    if(!workStarted){
      setWorkMessage("業務開始してから実施して下さい。");
      playBuzzer();
      return;
    }
    const upperBarcode = barcode.toUpperCase();
    const data =  memoryData.find(item => item.status === 0 && (item.jan === upperBarcode || item.original_num === upperBarcode));
    if(data){
      setInspection(data);
      setQuantity(data.quantity);
      playRing();
      //quantityRef.current?.open();
      setWorkMessage("");
    } else{
      setBarcode("");
      setQuantity(0);
      setInspection(undefined);
      setWorkMessage("バーコードがありません");
      playBuzzer();
      inputRef.current?.setFocus();
    }
  }

  const checkOrderCompletion = (orderNum: number, memoryInspections: Inspection[]) => {
    const orderRecords = memoryInspections.filter(item => item.order_num === orderNum && item.status === 0);
    if (orderRecords.length === 0) {
      const koStore = koStoreData.find(ks => ks.order_num === orderNum);
      const rackNum = koStore?.rack_num ?? '未登録';
      setWorkMessage(`商品揃いました。オーダー番号:${orderNum}, 仮置き倉庫棚番：${rackNum}`);
      playDeliverOk();
    }
    else {
      setWorkMessage(`オーダー番号:${orderNum}の未検品商品があります。`);
      playRing();
    }
  };

  const handleKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      if ( barcode !== "" ) {
        searchBarCode();
      }
    }
  };

  const handleSearch = async () => {
    if ( barcode !== "" ) {
      searchBarCode();
    }
  };

  const handleProcess = async () => {
    if (inspection) {
      if (quantity > inspection.quantity) {
        setWorkMessage(`入荷数量(${inspection.quantity})を超えています`);
        playBuzzer();
        return;
      }
      if (quantity <= 0) {
        setWorkMessage("数量は1以上を入力してください");
        playBuzzer();
        return;
      }

      let newHandledCount = handledCount;
      const updatedMemoryData = memoryData.map(item => {
        if (item.id !== inspection.id) return item;
        if(item.flag == 0) {
          newHandledCount = newHandledCount + 1;
        }
        const newQuantity = item.quantity - quantity;
        const newCurrentInspectionQuantity = item.download === 1 ? quantity : item.current_inspection_quantity + quantity;
        if (newQuantity === 0) {
          return { ...item, quantity: item.original_quantity, current_inspection_quantity: newCurrentInspectionQuantity, status: 1, flag: 1 };
        }
          return { ...item, quantity: newQuantity, current_inspection_quantity: newCurrentInspectionQuantity, flag: 1 };
      });
      setMemoryData(updatedMemoryData);
      setHandledCount(newHandledCount);

      // タスク 7.2: オーダー完了チェック
      checkOrderCompletion(inspection.order_num, updatedMemoryData);

      // UIリセット
      setBarcode('');
      setQuantity(0);
      setInspection(undefined);
      inputRef.current?.setFocus();
      //setTimeout(() => { inputRef.current?.setFocus(); }, 50);
    }
  };

  const handleClear = () => {
    setBarcode("");
    setQuantity(0);    
    setInspection(undefined);
    inputRef.current?.setFocus();
    //setTimeout(() => {inputRef.current?.setFocus();}, 50);
  }

  const fetchInspections = async () => {
    try {
      const response = await axiosInstance.get('/inspection');
      if (response.status === 200) {
        setCount(response.data.count)
        inputRef.current?.setFocus();
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
    }
  };

  //tmp code
  //useEffect(() => {
  //  if (isAuthenticated && location.pathname.includes("inspection")) {
  //    fetchInspections();
  //  }
  //}, [isAuthenticated, onTabChange]);

/**
 * 業務終了
 * メモリ → サーバー
 */
  const handleStartWork = async () => {
    if (workStarted) {
      setWorkMessage('既に業務開始しています。');
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.get('/inspections_getall');
      if(response.data.inspections.length == 0) {
        setWorkMessage('検品マスタに検品待ちデータがありません、業務開始できません。');
        playBuzzer();
        return;
      }
      const data = response.data.inspections.map((item:Inspection)=>({...item, flag:0 }));
      setMemoryData(data);
      setKoStoreData(response.data.ko_stores);
      setWorkMessage("業務開始が成功しました。");
      setTotalCount(response.data.inspections.length);
      setHandledCount(0);
      setWorkStarted(true);
      playRing();
      inputRef.current?.setFocus();
      //console.log("業務開始取得データ:");
      //console.log(data);
      //console.log(response.data.ko_stores);
    } catch (error) {
      setWorkMessage('通信エラー: データ取得に失敗しました');
      playBuzzer();
    } finally {
      setLoading(false);
    }
};

const handleEndWork = async () => {
  if (!workStarted) {
    setWorkMessage('業務開始されていません');
    return;
  }
  // 処理済みデータの抽出
  const updatedRecords = memoryData.filter(item => item.flag === 1);
  
  if (updatedRecords.length === 0) {
      // 処理済み0件: クリーンアップのみ
      setMemoryData([]);
      setKoStoreData([]);
      setWorkStarted(false);
      setWorkMessage('業務終了が成功しました。');
      playRing();
      return;
    }

    // 処理済みありの場合: サーバーへ送信
    setLoading(true);
    //console.log("業務終了データ:");
    //console.log(memoryData);
    //console.log(updatedRecords);
    try {
      const payload = updatedRecords.map(item => ({
        id: item.id,
        quantity: item.quantity,
        current_inspection_quantity: item.current_inspection_quantity,
        status: item.status
      }));

      const response = await axiosInstance.post('/inspections_processall/', { inspections: payload });
      setMemoryData([]);
      setKoStoreData([]);
      setWorkStarted(false);
      playRing();
      setWorkMessage('業務終了が成功しました。');
    } catch (error) {
      setWorkMessage('通信エラー: 送信に失敗しました。再度業務終了をお試しください。');
      playBuzzer();
    } finally {
      setLoading(false);
    }
};

  return (
    <IonPage ref={pageRef} id="inspection-page">
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>検品作業</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={true}>
        <IonGrid className="work-buttons">
          <IonRow>
          <IonCol>
            <IonButton expand="block" color="primary" size="large" onClick={handleStartWork} disabled={workStarted}> 業務開始 </IonButton>
          </IonCol>
          <IonCol>
            <IonButton expand="block" color="danger" size="large" onClick={handleEndWork} disabled={!workStarted}> 業務終了</IonButton>
          </IonCol>

          {/*
          <IonCol>
            <IonButton expand="block" color="success" size="large" onClick={handleFocusBarcode}> Focus </IonButton>
          </IonCol>
          */}
          </IonRow>
        </IonGrid>

        <IonItem>
          <div style={{width: '100%', textAlign: 'left', fontSize: '18px', fontWeight: 'bold' }}>
            {workStarted === true ? "検品マスタ件数: " + totalCount + "件" + ", 処理件数: " + handledCount + "件" : ""}
          </div>
        </IonItem>
        <IonItem>
          <div style={{width: '100%', textAlign: 'left', fontSize: '18px', fontWeight: 'bold' }}>
            {workMessage}
          </div>
        </IonItem>

        <audio id="buzzer" src="/assets/error.mp3" preload="auto"></audio>
        <audio id="ring" src="/assets/ok.mp3" preload="auto"></audio>
        <audio id="deliver_ok" src="/assets/deliver.mp3" preload="auto"></audio>
        <IonList>
          <IonItem>
            <IonInput label="バーコード" ref={inputRef} placeholder="0000000000000" value={barcode} onIonInput={handleBarcodeChange} onKeyDown={handleKeyPress}></IonInput>
            <IonButton color="primary" expand="full" slot='end' onClick={handleClear}>クリア</IonButton>
            <IonButton color="primary" expand="full" slot='end' onClick={handleSearch}>検　索</IonButton>
          </IonItem>
          <IonItem>
            <IonSelect label="入荷件数" ref={quantityRef} value={quantity} onIonChange={handleQuantityChange}>
              {Array.from({ length: 101 }, (_, i) => (
                <IonSelectOption key={i} value={i}>
                  {i}
                </IonSelectOption>
              ))}
            </IonSelect>
            <IonButton color="primary" expand="full" slot='end' onClick={handleProcess}>OK</IonButton>
          </IonItem>
        </IonList>

        {/*
        <IonListHeader style={{ fontWeight: 'bold', fontSize: '22px' }}>{segment === 'inspection' ? "検品マスタ : " + count + "件" : "検品作業完了"}</IonListHeader>
        */}

        <InspectionCard
          inspection={inspection}
          hide={segment === 'success'}
        />
        <div style={{ overflowY: 'auto', height: 'calc(100% - 110px)', display: segment === 'inspection' ? 'none' : '' }}>
          <InspectionList
            inspections={inspections}
          />
        </div>
        <IonLoading
          isOpen={loading}
          message={'Loading...'}
          onDidDismiss={() => setLoading(false)}
        />

        {/*
        <IonItem>
          {show_rack_num}
        </IonItem>
        */}

      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    mode: getConfig()!.get('mode'),
    isAuthenticated: state.user.isLoggedin,
  }),
  mapDispatchToProps: {
  },
  component: React.memo(InspectionPage),
});
